import {
  AlertCircle,
  Check,
  CheckCheck,
  Clock,
  HelpCircle,
  Loader2,
  type LucideIcon,
} from 'lucide-react';

import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  describeDeliveryStatus,
  type DeliveryFields,
  type DeliveryStatusKind,
} from '@/features/chat/delivery-status';
import { cn } from '@/lib/utils';

/**
 * Glyph + tone per status kind — spec 02 §3 ("Bubble" column), 2026-09
 * honest send status:
 *
 *   pending   spinner                 (client-only optimistic send)
 *   queued    clock                   (not yet handed to WhatsApp)
 *   sent      one tick, muted
 *   delivered two ticks, muted
 *   read      two ticks, BLUE
 *   failed    rose alert circle       (+ Retry in the footer)
 *   unknown   amber question mark     (+「仍要重发…」 behind a confirm)
 *   other     muted question mark     — a status this console does not know
 *
 * Muted glyphs inherit the footer's faint tone. `other` is deliberately a
 * question mark: the pre-F2 icon drew every unrecognised status as the
 * single "sent" check (bug 4).
 */
const GLYPHS: Record<DeliveryStatusKind, { Icon: LucideIcon; tone: string }> = {
  pending: { Icon: Loader2, tone: 'animate-spin' },
  queued: { Icon: Clock, tone: '' },
  sent: { Icon: Check, tone: '' },
  delivered: { Icon: CheckCheck, tone: '' },
  read: { Icon: CheckCheck, tone: 'text-sky-500' },
  failed: { Icon: AlertCircle, tone: 'text-rose-600' },
  unknown: { Icon: HelpCircle, tone: 'text-amber-600 dark:text-amber-400' },
  other: { Icon: HelpCircle, tone: 'opacity-70' },
};

/**
 * The delivery-status glyph in an outbound bubble's footer, with its meaning
 * on hover (status, receipt times, the error text — `describeDeliveryStatus`
 * owns the copy). The same text is the glyph's accessible name, so the
 * status is readable without hovering too.
 *
 * `data-status-kind` names the drawn kind (the bubble root keeps the raw
 * wire value on `data-delivery-status`).
 */
export function DeliveryStatusIcon({
  message,
}: {
  message: DeliveryFields;
}): React.JSX.Element {
  const { kind, label, details } = describeDeliveryStatus(message);
  const { Icon, tone } = GLYPHS[kind];
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          role="img"
          aria-label={label}
          data-testid="delivery-status"
          data-status-kind={kind}
          className="inline-flex shrink-0"
        >
          <Icon aria-hidden="true" className={cn('size-3', tone)} />
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">
        <p>{label}</p>
        {details.map((line) => (
          <p key={line}>{line}</p>
        ))}
      </TooltipContent>
    </Tooltip>
  );
}
